<script setup lang="ts">
/**
 * Stuff tab — performer stats report. Fetches `GET /persons/performers/stats`
 * for a date range and shows one entry per performer: name, then a stacked
 * stat summary (business services count, total income, unique clients)
 * above a dated line-item list of every business service delivered, with
 * the price earned and gender balance per service.
 *
 * Desktop matches `docs/sketches/desktop/performers-stat-table.html` (a
 * two-column DynamicTable); mobile matches
 * `docs/sketches/mobile/performers-stat-table.html` (one card per
 * performer, stacked full-width) — same
 * `rows`/`reload()`/CSV export, just two render paths, mirroring
 * `FinancesTab`'s desktop/mobile split.
 *
 * Performers with zero business services in the period are excluded
 * entirely (not flagged, unlike either sketch) — an empty report for a
 * performer isn't useful here.
 *
 * Loading is entirely manual, mirroring `FinancesTab`: nothing refetches on
 * date-input change, only the Reload button calls the API, so dates can be
 * adjusted before spending a round-trip. Download CSV exports exactly the
 * last-loaded range.
 */
import { computed, markRaw, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import { fetchPerformerStats, type PerformerBusinessService } from '@/api/performerStats'
import {
  fetchBusinessServiceTypes,
  type BusinessServiceTypesGroup,
} from '@/api/businessServiceTypes'
import { majorToMinor } from '@/utils/majorToMinor'
import { formatMoney } from '@/utils/formatMoney'
import { formatDateTime } from '@/utils/formatDateTime'
import { groupAgeMonths } from '@/utils/groupAge'
import { fetchGroups, type Group } from '@/api/groups'
import { useGroupDisplay } from '@/composables/useGroupDisplay'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import PerformerNameCell from '@/components/table/cell/PerformerNameCell.vue'
import PerformerStatCell from '@/components/table/cell/PerformerStatCell.vue'
import StuffMobileCardCell from '@/components/table/cell/StuffMobileCardCell.vue'

const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()
const { displayName: groupDisplayName } = useGroupDisplay()

interface PageLike<T> {
  // Minimal shape shared by every cursor-paginated response this tab drains.
  items: T[]
  has_next_page: boolean
  next_cursor: number | null
}

/** Repeatedly calls `fetchPage` until `has_next_page` is false, returning every item across all pages. */
async function drainAllPages<T>(
  fetchPage: (cursor?: number) => Promise<PageLike<T>>,
): Promise<T[]> {
  const all: T[] = []
  let cursor: number | undefined
  let hasNextPage = true
  while (hasNextPage) {
    const page = await fetchPage(cursor)
    all.push(...page.items)
    hasNextPage = page.has_next_page
    cursor = page.next_cursor ?? undefined
  }
  return all
}

interface StuffRow {
  // One rendered table row: a performer with their period stats, pre-formatted for display.

  performerId: string
  rowNumber: number
  name: string
  businessServicesCnt: number
  totalIncome: number
  totalIncomeFormatted: string
  uniqueClientsCnt: number
  businessServices: {
    date: string
    dateFormatted: string
    serviceName: string
    price: number
    priceFormatted: string
    maleClientsCnt: number
    femaleClientsCnt: number
    groupName: string
    // Group display name; '' for an ad-hoc service (or when groups couldn't be loaded).
    groupAge: string
    // Group age at the end of the period ("1 г. 5 м."); '' when there is no group.
  }[]
}

/** `Date` → `YYYY-MM-DD` using local calendar fields (not `toISOString`, which would shift the date across a UTC day boundary for any non-UTC timezone). */
function isoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
function firstOfPreviousMonthIso(): string {
  const now = new Date()
  return isoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1))
}
/** Day 0 of the current month rolls back to the last day of the previous month (JS Date semantics). */
function lastOfPreviousMonthIso(): string {
  const now = new Date()
  return isoDate(new Date(now.getFullYear(), now.getMonth(), 0))
}

// Defaults to the full previous calendar month. Unlike Finances'
// client-visit-counts endpoint (exclusive end), performer stats' end_date
// is inclusive (see person_endpoints.py), so the default end is the last
// day of the previous month, not the first of the current one.
const startDate = ref(firstOfPreviousMonthIso())
const endDate = ref(lastOfPreviousMonthIso())

const loading = ref(false)
const error = ref<string | undefined>()
const rows = ref<StuffRow[]>([])
/** The date range actually behind the currently displayed `rows` — distinct from the (possibly since-edited) date inputs, so the CSV filename always matches what's on screen. */
const loadedRange = ref<{ start: string; end: string } | null>(null)

function formatPrice(amount: number): string {
  const minorUnits = majorToMinor(amount) ?? 0
  return formatMoney({ amount: minorUnits, includeCurrency: true }).formatted
}

function formatServiceDate(date: string): string {
  return formatDateTime({ date, dateStyle: 'medium' }).formattedDate
}

/** "1 г. 5 м." / "5 м." / "< 1 м." — the group's age as of the period's end. */
function formatGroupAge(start: string, asOf: string): string {
  const months = groupAgeMonths(start, asOf)
  if (months < 1) return t('common.ageUnderMonth')
  const years = Math.floor(months / 12)
  const rest = months % 12
  const parts: string[] = []
  if (years > 0) parts.push(t('common.ageYears', { n: years }))
  if (rest > 0) parts.push(t('common.ageMonths', { n: rest }))
  return parts.join('\u00a0')
}

function toRowServices(
  services: PerformerBusinessService[],
  typeNameById: Map<string, string>,
  groupById: Map<string, Group>,
  asOf: string,
): StuffRow['businessServices'] {
  return services
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((bs) => {
      const group = bs.group_id ? groupById.get(bs.group_id) : undefined
      return {
        date: bs.date,
        dateFormatted: formatServiceDate(bs.date),
        serviceName:
          typeNameById.get(bs.business_service_type_id) ??
          t('common.typeFallback', { id: bs.business_service_type_id }),
        price: bs.price,
        priceFormatted: formatPrice(bs.price),
        maleClientsCnt: bs.male_clients_cnt,
        femaleClientsCnt: bs.female_clients_cnt,
        groupName: group ? groupDisplayName(group) : '',
        groupAge: group ? formatGroupAge(group.start, asOf) : '',
      }
    })
}

/** Bumped on every `reload()` so a slower, superseded request can't overwrite a newer one's rows. */
let reloadSeq = 0

async function reload() {
  const seq = ++reloadSeq
  loading.value = true
  error.value = undefined
  const requestedStart = startDate.value
  const requestedEnd = endDate.value
  try {
    const [items, serviceTypeGroups, groups] = await Promise.all([
      drainAllPages((cursor) => fetchPerformerStats(requestedStart, requestedEnd, cursor)),
      // Business service types are only needed for the enrichment of the service names.
      // They require separate permissions.
      hasRole(ResourceAccessRole.BusinessServiceTypesView)
        ? drainAllPages<BusinessServiceTypesGroup>((cursor) => fetchBusinessServiceTypes(cursor))
        : Promise.resolve<BusinessServiceTypesGroup[]>([]),
      // Groups are likewise enrichment only (Group / Age columns), gated on GroupsView.
      hasRole(ResourceAccessRole.GroupsView)
        ? drainAllPages<Group>((cursor) => fetchGroups(cursor))
        : Promise.resolve<Group[]>([]),
    ])
    const groupById = new Map(groups.map((g) => [g.id, g]))

    const typeNameById = new Map<string, string>()
    for (const group of serviceTypeGroups) {
      for (const type of group.business_service_types) {
        const label = type.description.trim()
        typeNameById.set(type.id, label !== '' ? label : t('common.typeFallback', { id: type.id }))
      }
    }

    const builtRows = items
      // Performers with no business services in the period are excluded
      // entirely rather than shown/flagged — an empty report row isn't useful.
      .filter((item) => item.stat.business_services_cnt > 0)
      .map((item) => ({
        performerId: item.person.id,
        name: item.person.name,
        businessServicesCnt: item.stat.business_services_cnt,
        totalIncome: item.stat.total_income,
        totalIncomeFormatted: formatPrice(item.stat.total_income),
        uniqueClientsCnt: item.stat.unique_clients_cnt,
        businessServices: toRowServices(
          item.stat.business_services,
          typeNameById,
          groupById,
          requestedEnd,
        ),
      }))
    builtRows.sort((a, b) => a.name.localeCompare(b.name, 'ru'))

    if (seq !== reloadSeq) return
    rows.value = builtRows.map((row, index) => ({ ...row, rowNumber: index + 1 }))
    loadedRange.value = { start: requestedStart, end: requestedEnd }
  } catch (e) {
    if (seq !== reloadSeq) return
    error.value = e instanceof Error ? e.message : t('tabs.stuff.loadError')
  } finally {
    if (seq === reloadSeq) loading.value = false
  }
}

onMounted(() => {
  void reload()
})

// The service-type and group lookups are gated on roles, which are empty
// until the user info has loaded (e.g. on a hard refresh) — a reload that
// started before then would show "Type №<id>" and no groups. Reload once the
// roles that decide what gets fetched change.
watch(
  () => [
    hasRole(ResourceAccessRole.BusinessServiceTypesView),
    hasRole(ResourceAccessRole.GroupsView),
  ],
  () => void reload(),
)

const columns = computed<Column<StuffRow>[]>(() => [
  {
    key: 'name',
    label: t('tabs.stuff.colPerformer'),
    component: markRaw(PerformerNameCell),
    props: (row) => ({
      rowNumber: row.rowNumber,
      name: row.name,
      businessServicesCnt: row.businessServicesCnt,
      totalIncomeFormatted: row.totalIncomeFormatted,
      uniqueClientsCnt: row.uniqueClientsCnt,
    }),
    width: '11rem',
  },
  {
    key: 'stat',
    label: t('tabs.stuff.colServicesDelivered'),
    component: markRaw(PerformerStatCell),
    props: (row) => ({
      businessServices: row.businessServices,
    }),
  },
])

/**
 * Single wide column for the mobile table: each row's `<td>` is a full
 * performer "card" (name, stat summary, and a nested DynamicTable
 * breakdown) via StuffMobileCardCell — same `rows` data as `columns`
 * above, just rendered through one rich cell instead of two.
 */
const mobileColumns = computed<Column<StuffRow>[]>(() => [
  {
    key: 'card',
    label: t('tabs.stuff.colPerformer'),
    component: markRaw(StuffMobileCardCell),
    props: (row) => ({
      name: row.name,
      businessServicesCnt: row.businessServicesCnt,
      totalIncomeFormatted: row.totalIncomeFormatted,
      uniqueClientsCnt: row.uniqueClientsCnt,
      businessServices: row.businessServices,
    }),
  },
])

function escapeCsvField(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function downloadCsv() {
  if (loadedRange.value === null) return
  const header = [
    '#',
    t('tabs.stuff.colPerformer'),
    t('common.date'),
    t('common.service'),
    t('common.group'),
    t('tabs.stuff.colGroupAge'),
    t('common.earned'),
    t('common.male'),
    t('common.female'),
  ]
  const csvRows: string[][] = [header]
  rows.value.forEach((row) => {
    row.businessServices.forEach((bs, i) => {
      csvRows.push([
        i === 0 ? String(row.rowNumber) : '',
        i === 0 ? row.name : '',
        bs.date,
        bs.serviceName,
        bs.groupName,
        bs.groupAge,
        bs.price.toFixed(2),
        String(bs.maleClientsCnt),
        String(bs.femaleClientsCnt),
      ])
    })
  })
  const csvContent = csvRows.map((row) => row.map(escapeCsvField).join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `performers-stat_${loadedRange.value.start}_${loadedRange.value.end}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
</script>

<template>
  <article class="stuff-tab" :class="{ 'stuff-tab--mobile': isMobile }">
    <header v-if="!isMobile" class="stuff-header">
      <div>
        <h1>{{ t('tabs.stuff.title') }}</h1>
        <p v-if="loadedRange" class="stuff-period">
          {{ t('tabs.stuff.periodDesktop', { start: loadedRange.start, end: loadedRange.end }) }}
        </p>
      </div>
      <div class="stuff-actions">
        <div class="stuff-date-range">
          <label for="stuff-start">{{ t('common.from') }}</label>
          <input id="stuff-start" v-model="startDate" type="date" />
          <label for="stuff-end">{{ t('common.to') }}</label>
          <input id="stuff-end" v-model="endDate" type="date" />
        </div>
        <button
          type="button"
          class="stuff-btn stuff-btn--reload"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? t('common.loadingShort') : t('common.reload') }}
        </button>
        <button
          type="button"
          class="stuff-btn stuff-btn--download"
          :disabled="loading || loadedRange === null"
          @click="downloadCsv"
        >
          {{ t('common.downloadCsv') }}
        </button>
      </div>
    </header>

    <template v-else>
      <header class="stuff-mobile-header">
        <h1>{{ t('tabs.stuff.title') }}</h1>
        <p v-if="loadedRange" class="stuff-period">
          {{ t('tabs.stuff.periodMobile', { start: loadedRange.start, end: loadedRange.end }) }}
        </p>
      </header>
      <div class="stuff-date-range stuff-date-range--mobile">
        <label for="stuff-start-mobile">{{ t('common.from') }}</label>
        <input id="stuff-start-mobile" v-model="startDate" type="date" />
        <span class="stuff-mobile-arrow" aria-hidden="true">→</span>
        <input id="stuff-end-mobile" v-model="endDate" type="date" :aria-label="t('common.to')" />
      </div>
      <div class="stuff-actions stuff-actions--mobile">
        <button
          type="button"
          class="stuff-btn stuff-btn--reload"
          :disabled="loading"
          @click="reload"
        >
          {{ loading ? t('common.loadingShort') : t('common.reload') }}
        </button>
        <button
          type="button"
          class="stuff-btn stuff-btn--download"
          :disabled="loading || loadedRange === null"
          @click="downloadCsv"
        >
          {{ t('common.downloadCsv') }}
        </button>
      </div>
    </template>

    <p v-if="error" class="text-red-600">{{ error }}</p>

    <template v-if="!error">
      <div
        v-if="!isMobile"
        class="stuff-scroll-wrap"
        :class="{ 'stuff-scroll-wrap--loading': loading && rows.length > 0 }"
      >
        <DynamicTable
          v-if="rows.length > 0"
          class="stuff-table"
          :columns="columns as Column<unknown>[]"
          :data="rows"
          pinnable-header
          storage-key="stuff"
        />
        <p v-else-if="loading" class="text-tufte-muted stuff-empty">
          {{ t('tabs.stuff.loadingFull') }}
        </p>
        <p v-else class="stuff-empty">{{ t('tabs.stuff.empty') }}</p>
      </div>

      <div
        v-else
        class="stuff-cards"
        :class="{ 'stuff-cards--loading': loading && rows.length > 0 }"
      >
        <DynamicTable
          v-if="rows.length > 0"
          :columns="mobileColumns as Column<unknown>[]"
          :data="rows"
          :pinnable-header="false"
        />
        <p v-else-if="loading" class="text-tufte-muted stuff-empty">
          {{ t('tabs.stuff.loadingFull') }}
        </p>
        <p v-else class="stuff-empty">{{ t('tabs.stuff.empty') }}</p>
      </div>
    </template>
  </article>
</template>

<style scoped>
.stuff-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--color-tufte-border);
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

.stuff-period {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-top: 0.2rem;
}

.stuff-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.stuff-date-range {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
}

.stuff-date-range input[type='date'] {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-text);
  background: var(--color-tufte-bg);
  border: 1px solid var(--color-tufte-border);
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  outline: none;
}

.stuff-date-range input[type='date']:focus {
  border-color: color-mix(in srgb, var(--color-tufte-text) 55%, transparent);
}

.stuff-btn {
  appearance: none;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  border-radius: 6px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  border: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  color: var(--color-tufte-text);
}

.stuff-btn:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-tufte-border) 25%, var(--color-tufte-bg));
}

.stuff-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.stuff-btn--download {
  background: var(--color-tufte-text);
  color: var(--color-tufte-bg);
  border-color: var(--color-tufte-text);
}

.stuff-btn--download:hover:not(:disabled) {
  background: color-mix(in srgb, var(--color-tufte-text) 85%, transparent);
}

.stuff-scroll-wrap {
  overflow-x: auto;
  margin-top: 1rem;
}

/* Fill the content column like the other tabs' tables; the services column takes the leftover width. */
.stuff-table :deep(.dynamic-table) {
  width: 100%;
}

.stuff-scroll-wrap--loading {
  opacity: 0.6;
}

.stuff-empty {
  padding: 1.5rem 0.75rem;
  text-align: center;
  color: var(--color-tufte-muted);
  font-family: var(--font-tufte-sans);
  border: 1px solid var(--color-tufte-border);
  margin-top: 1rem;
}

/*
 * Mobile layout (see `docs/sketches/mobile/performers-stat-table.html`): title/date-range/
 * actions stack full-width above a card list instead of sharing the desktop
 * table's two-column grid — same `MobileMain.vue` zeroed-padding convention
 * as `FinancesTab`, so each section here carries its own 1.25rem horizontal
 * padding.
 */
.stuff-mobile-header {
  padding: 0.75rem 1.25rem;
}

.stuff-date-range--mobile {
  padding: 0 1.25rem 0.75rem;
  border-bottom: 1px solid var(--color-tufte-border);
  margin-bottom: 0.75rem;
}

.stuff-date-range--mobile input[type='date'] {
  flex: 1;
  min-width: 0;
}

.stuff-mobile-arrow {
  color: var(--color-tufte-muted);
  flex-shrink: 0;
}

/* Sticks to the top of `.mobile-main`'s scroll container once the header/date-range scroll out of view, same technique as FinancesTab's `.finances-actions--mobile`. */
.stuff-actions--mobile {
  padding: 0.75rem 1.25rem;
  flex-wrap: nowrap;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
}

.stuff-actions--mobile .stuff-btn {
  flex: 1;
}

.stuff-cards--loading {
  opacity: 0.6;
}

/*
 * The mobile DynamicTable's single column is StuffMobileCardCell, which
 * renders full-card padding/name/summary/breakdown itself — nothing to
 * style here beyond the loading-state dimming above; DynamicTable's own
 * row border already separates one performer's card from the next.
 */

.stuff-tab--mobile > .text-red-600,
.stuff-tab--mobile .stuff-empty {
  margin-left: 1.25rem;
  margin-right: 1.25rem;
}
</style>
